import React from "react";
import { Link } from "react-router-dom";
import { Activity, FileText, Share2, ShieldCheck, Clock, QrCode, ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";

const HERO = "https://images.unsplash.com/photo-1758691462774-f01ed567f2c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBob2xkaW5nJTIwdGFibGV0JTIwaG9zcGl0YWx8ZW58MHx8fHwxNzgyNjgxNDY4fDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: FileText, title: "Digital referral forms", desc: "Replace handwritten forms with standardized, professional radiology requests." },
  { icon: QrCode, title: "Branded PDFs with QR", desc: "Generate clean PDFs with your clinic branding and a secure QR link to the referral." },
  { icon: Share2, title: "Share instantly", desc: "Download, email, or send via WhatsApp to patients and imaging centres in seconds." },
  { icon: Clock, title: "Under one minute", desc: "Templates and saved details mean a complete referral in less than 60 seconds." },
  { icon: ShieldCheck, title: "Secure & auditable", desc: "Role-based access, encrypted passwords, audit logs and unique referral IDs." },
  { icon: Activity, title: "Connected centres", desc: "Registered radiology centres receive referrals directly in their dashboard." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <header className="px-6 md:px-12 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="text-xl font-bold tracking-tight text-slate-900 font-head">ReferU</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login"><Button data-testid="nav-login-btn" variant="ghost" className="rounded-xl">Log in</Button></Link>
          <Link to="/register"><Button data-testid="nav-register-btn" className="rounded-xl bg-blue-600 hover:bg-blue-700">Get started</Button></Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Digital Radiology Referrals</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 font-head leading-[1.05]">
            Radiology referrals made <span className="text-blue-600">simple.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl">
            Create professional digital radiology referrals and prescriptions, choose a centre or pharmacy, and instantly generate a branded PDF you can share via WhatsApp or email — in under a minute.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register"><Button data-testid="hero-start-btn" className="rounded-xl h-12 px-7 text-base bg-blue-600 hover:bg-blue-700">Start 14-day free trial <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
            <Link to="/login"><Button data-testid="hero-login-btn" variant="outline" className="rounded-xl h-12 px-7 text-base border-slate-300">I have an account</Button></Link>
          </div>
          <p className="mt-4 text-sm text-slate-500"><Check className="w-4 h-4 inline text-emerald-500 mr-1" /> No credit card required for trial</p>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-blue-100/50 rounded-[2rem] -rotate-2" />
          <img src={HERO} alt="Doctor reviewing radiology scan" className="relative rounded-3xl shadow-2xl w-full object-cover h-[380px] md:h-[460px]" />
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-head text-center">Everything you need to go paperless</h2>
          <p className="text-slate-600 text-center mt-3 max-w-2xl mx-auto">A modern referral platform built for clinics, doctors, and radiology centres.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 font-head">{f.title}</h3>
                <p className="text-slate-600 mt-2 text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-head">Simple, professional pricing</h2>
        <div className="mt-8 bg-white rounded-2xl border-2 border-blue-200 p-8 shadow-md text-left">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">ReferU Professional</p>
          <div className="flex items-end gap-1 mt-3">
            <span className="text-5xl font-bold text-slate-900 font-head">$5.99</span>
            <span className="text-slate-500 mb-2">/month</span>
          </div>
          <ul className="mt-6 space-y-2.5">
            {["Unlimited digital referrals", "PDF generation & QR codes", "WhatsApp & email sharing", "Saved templates & history", "Clinic branding & digital signature", "Secure referral links"].map((i) => (
              <li key={i} className="flex items-center gap-2 text-slate-700"><Check className="w-5 h-5 text-emerald-500" /> {i}</li>
            ))}
          </ul>
          <Link to="/register"><Button data-testid="pricing-start-btn" className="mt-8 w-full rounded-xl h-12 bg-blue-600 hover:bg-blue-700">Start your free trial</Button></Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        ReferU by Scrilcon · Digital Healthcare Referrals Made Simple
      </footer>
    </div>
  );
}
